import { defineStore } from "pinia";
import { ref } from "vue";

import { type BasePaginationData } from "shared/components/base/BasePagination";
import { type ContactType } from "shared/composables/useContact";
import type * as sideBars from "shared/modules/SideBar/components";
import { type Nullable } from "shared/types";
import { type Mention } from "shared/types/mentions";

export type SideBarComponent = keyof typeof sideBars;

export interface SideBarContentData {
  author?: string;
  hideCloseButton?: boolean;
  loading?: boolean;
  matches?: ContactType[];
  mention?: Mention;
  newDesign?: boolean;
  onPaginationChange?: (pagination: BasePaginationData) => void;
  pagination?: BasePaginationData;
  source?: "database_matches";
  streamId?: number;
}

export interface SideBarContent {
  component?: Nullable<SideBarComponent>;
  data?: Nullable<SideBarContentData>;
  hideHeader?: boolean;
  slotComponent?: string;
  subtitle?: string;
  title?: string;
  width?: string;
}

const DEFAULT_WIDTH = "380px";

export const useSideBarStore = defineStore("sideBar", () => {
  const sideBarComponent = ref<Nullable<SideBarComponent>>(null);
  const sideBarSlotComponent = ref<string>("");
  const sideBarData = ref<Nullable<SideBarContentData>>(null);
  const sideBarOpen = ref<boolean>(false);
  const sideBarSubtitle = ref<string>("");
  const sideBarTitle = ref<string>("");
  const sideBarHideHeader = ref<boolean>(false);
  const sideBarWidth = ref<string>(DEFAULT_WIDTH);

  function showSideBar() {
    sideBarOpen.value = true;
  }

  function hideSideBar() {
    sideBarOpen.value = false;
  }

  function setSideBarContent({
    component,
    data,
    hideHeader,
    slotComponent,
    subtitle,
    title,
    width,
  }: SideBarContent) {
    sideBarComponent.value = component || null;
    sideBarSlotComponent.value = slotComponent || "";
    sideBarData.value = data || null;
    sideBarSubtitle.value = subtitle || "";
    sideBarTitle.value = title || "";
    sideBarHideHeader.value = Boolean(hideHeader);
    sideBarWidth.value = width || DEFAULT_WIDTH;
  }

  return {
    hideSideBar,
    setSideBarContent,
    showSideBar,
    sideBarComponent,
    sideBarData,
    sideBarHideHeader,
    sideBarOpen,
    sideBarSlotComponent,
    sideBarSubtitle,
    sideBarTitle,
    sideBarWidth,
  };
});
